import { DatePicker, Input, TextArea, TimePicker } from "@/components/form";
import Modal from "@/components/Modal";
import { useAppSelector } from "@/hooks";
import { getProjectDetailService } from "@/service/project.service";
import {
  selectTimeEntriesLoading,
  TimeEntryDisplay,
} from "@/store/slices/timeEntriesSlice";
import { ProjectList } from "@/types/project.model";
import moment from "moment";
import React, { useRef } from "react";
import { useForm } from "react-hook-form";

export interface EditTimeEntryFormData {
  id?: string;
  clock_in_date: string;
  clock_in_time: string;
  clock_out_date: string;
  clock_out_time: string;
  duration?: string;
  notes: string;
  billable: boolean;
}

interface EditTimeEntryFormProps {
  initialData: TimeEntryDisplay;
  onSubmit: (data: EditTimeEntryFormData) => void;
  onCancel: () => void;
}

const EditTimeEntryForm: React.FC<EditTimeEntryFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
}) => {
  const isLoading = useAppSelector(selectTimeEntriesLoading);

  // Set default values
  let defaultValues: Partial<EditTimeEntryFormData> = {
    clock_in_date: "",
    clock_in_time: "",
    clock_out_date: "",
    clock_out_time: "",
    duration: "00:00",
    notes: "",
    billable: false,
  };

  if (initialData) {
    defaultValues = {
      id: initialData.id,
      clock_in_date: initialData.clock_in_date,
      clock_in_time: initialData.clock_in_time,
      clock_out_date: initialData.clock_out_date,
      clock_out_time: initialData.clock_out_time,
      notes: initialData.notes,
      billable: initialData.billable,
    };
  }

  // No need for separate state since we're using the form's state

  // Initialize form
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
    trigger,
  } = useForm<EditTimeEntryFormData>({
    defaultValues,
    mode: "all",
    criteriaMode: "firstError",
    reValidateMode: "onChange",
  });

  // Track if the form has been modified
  const [isFormModified, setIsFormModified] = React.useState(false);
  const [showClearTimeModal, setShowClearTimeModal] = React.useState(false);
  const durationRef = useRef<HTMLInputElement>(null);

  const [selectedProject, setSelectedProject] =
    React.useState<ProjectList | null>(null);

  // Watch all form values to detect changes
  const formValues = watch();

  // Check if form values have changed from initial data
  React.useEffect(() => {
    if (initialData) {
      // Compare current form values with initial data
      const hasChanged =
        formValues.clock_in_date !== initialData.clock_in_date ||
        formValues.clock_in_time !== initialData.clock_in_time ||
        formValues.clock_out_date !== initialData.clock_out_date ||
        formValues.clock_out_time !== initialData.clock_out_time ||
        formValues.duration !== initialData.duration ||
        formValues.notes !== initialData.notes ||
        formValues.billable !== initialData.billable;

      setIsFormModified(hasChanged);
    }
  }, [formValues, initialData]);

  React.useEffect(() => {
    if (initialData && initialData.project_id) {
      const fetchProject = async () => {
        try {
          const {
            data: { data },
          } = await getProjectDetailService(initialData.project_id as string);
          setSelectedProject(data);
        } catch (error) {
          console.error("Error fetching project:", error);
        }
      };

      fetchProject();
    }
  }, [initialData]);

  // Check if duration exceeds remaining hours
  const checkIfDurationExceedsRemainingHours = (duration: string): boolean => {
    if (!selectedProject) return false;

    // Parse the duration (format: HH:MM)
    const durationParts = duration.split(":");
    const hours = parseInt(durationParts[0], 10) || 0;
    const minutes = parseInt(durationParts[1], 10) || 0;

    // Convert to hours
    const durationHours = hours + minutes / 60;

    // Get remaining hours from project
    const remainingHours =
      (selectedProject.tracker?.total_esti_hrs as number) -
      (selectedProject.util_hrs as number);

    // Check if duration exceeds remaining hours
    return durationHours > remainingHours;
  };

  // Calculate duration based on clock-in and clock-out date and time
  const calculateDuration = () => {
    const clockInDate = watch("clock_in_date");
    const clockInTime = watch("clock_in_time");
    const clockOutDate = watch("clock_out_date");
    const clockOutTime = watch("clock_out_time");

    // Only calculate if all values are present
    if (clockInDate && clockInTime && clockOutDate && clockOutTime) {
      // Parse the dates and times
      const clockIn = moment(
        `${clockInDate} ${clockInTime}`,
        "YYYY-MM-DD hh:mm A"
      );
      const clockOut = moment(
        `${clockOutDate} ${clockOutTime}`,
        "YYYY-MM-DD hh:mm A"
      );

      // Calculate the duration in minutes
      const durationMinutes = clockOut.diff(clockIn, "minutes");

      // Handle negative duration (if clock-out is before clock-in)
      if (durationMinutes < 0) {
        return "00:00";
      }

      // Convert to hours and minutes
      const hours = Math.floor(durationMinutes / 60);
      const minutes = durationMinutes % 60;

      // Format as HH:MM
      const formattedDuration = `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}`;
      return formattedDuration;
    }

    return "00:00";
  };

  // Modal state for exceeding hours
  const [isExceedingHoursModalOpen, setIsExceedingHoursModalOpen] =
    React.useState(false);
  const [pendingSubmitData, setPendingSubmitData] =
    React.useState<EditTimeEntryFormData | null>(null);

  // Intercept submit to check duration
  const handleFormSubmit = (data: EditTimeEntryFormData) => {
    // Use either calculated duration or manually entered duration
    const allTimeFieldsFilled =
      data.clock_in_date &&
      data.clock_in_time &&
      data.clock_out_date &&
      data.clock_out_time;
    const duration = allTimeFieldsFilled
      ? calculateDuration()
      : data.duration || "00:00";

    // Add duration to the data being submitted
    const dataWithDuration = {
      ...data,
      duration: duration,
    };

    if (checkIfDurationExceedsRemainingHours(duration)) {
      setPendingSubmitData(dataWithDuration);
      setIsExceedingHoursModalOpen(true);
      return;
    }
    onSubmit(dataWithDuration);
  };

  const handleClearTime = () => {
    setShowClearTimeModal(false);

    setValue("clock_in_time", "");
    setValue("clock_out_date", "");
    setValue("clock_out_time", "");
    setValue(
      "duration",
      initialData?.duration && typeof initialData?.duration === "string"
        ? initialData?.duration.split(" ")[0]
        : "00:00"
    );

    // Use setTimeout to ensure the DOM has updated before focusing
    setTimeout(() => {
      durationRef.current?.focus();
    }, 100);
  };

  return (
    <>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <div className="mb-5 px-4 py-2 overflow-y-auto max-h-[calc(100vh-200px)]">
          <p className="text-[14px] text-[#6C6C6C] mb-2">
            <span className="text-[#DC2626]">*</span> indicates mandatory fields
          </p>

          <div>
            <Input
              label="Employee/contractor name"
              id="name"
              placeholder="Employee name"
              readOnly={true}
              maxLength={50}
              value={initialData.user_name}
              className="text-[#6C6C6C]"
            />
          </div>
          <div>
            <Input
              label="Client name"
              id="name"
              placeholder="Client name"
              readOnly={true}
              maxLength={50}
              value={initialData.client_name}
              className="text-[#6C6C6C]"
            />
          </div>
          <div>
            <Input
              label="Project name"
              id="name"
              placeholder="Project name"
              readOnly={true}
              maxLength={50}
              value={initialData.project_name}
              className="text-[#6C6C6C]"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative w-full">
              <DatePicker
                label="Clock in date"
                required
                error={errors.clock_in_date?.message}
                className="w-full"
                value={watch("clock_in_date")}
                onChange={(value) => {
                  setValue("clock_in_date", value);
                  trigger("clock_in_date");
                }}
                showAbove={true}
                placeholder="Choose date"
              />
              <input
                type="hidden"
                {...register("clock_in_date", {
                  required: "Clock in date is required",
                })}
              />
            </div>

            <div className="relative w-full">
              <div className="relative w-full">
                <TimePicker
                  label="Clock in time"
                  value={watch("clock_in_time")}
                  className="w-full h-[42px] px-3 !border !border-[#E0E0E0] rounded-[6px]"
                  onChange={(value) => {
                    setValue("clock_in_time", value);
                    trigger("clock_in_time");
                  }}
                  // showAbove={true}
                  placeholder="Select time"
                  required={!watch("duration") || watch("duration") === "00:00"}
                />
              </div>

              <input
                type="hidden"
                {...register("clock_in_time", {
                  required:
                    !watch("duration") || watch("duration") === "00:00"
                      ? "Clock in time is required"
                      : false,
                })}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative w-full">
              <DatePicker
                label="Clock out date"
                required={
                  Boolean(watch("clock_in_time")) &&
                  Boolean(watch("clock_out_time"))
                }
                error={errors.clock_out_date?.message}
                className="w-full"
                value={watch("clock_out_date")}
                onChange={(value) => {
                  setValue("clock_out_date", value);
                  trigger("clock_out_date");
                }}
                showAbove={true}
                placeholder="Choose date"
                clearable
              />
              <input
                type="hidden"
                {...register("clock_out_date", {
                  required:
                    Boolean(watch("clock_in_time")) &&
                    Boolean(watch("clock_out_time"))
                      ? "Clock out date is required"
                      : false,
                })}
              />
            </div>
            <div className="relative w-full">
              <div className="relative w-full">
                <TimePicker
                  label="Clock out time"
                  value={watch("clock_out_time")}
                  className="w-full h-[42px] px-3 !border !border-[#E0E0E0] rounded-[6px]"
                  onChange={(value) => {
                    setValue("clock_out_time", value);
                    trigger("clock_out_time");
                  }}
                  // showAbove={true}
                  placeholder="Select time"
                  required={!watch("duration") || watch("duration") === "00:00"}
                />
              </div>

              <input
                type="hidden"
                {...register("clock_out_time", {
                  required:
                    !watch("duration") || watch("duration") === "00:00"
                      ? "Clock out time is required"
                      : false,
                })}
              />
            </div>
          </div>

          <div className="relative">
            <button
              type="button"
              className="absolute top-0 left-16 text-[14px] text-[#1068bf] cursor-pointer underline"
              onClick={() => setShowClearTimeModal(true)}
            >
              Edit
            </button>
            {/* Duration field - editable when not all time fields are filled */}
            {watch("clock_in_date") &&
            !watch("clock_in_time") &&
            !watch("clock_out_date") &&
            !watch("clock_out_time") ? (
              <Input
                ref={durationRef}
                label="Duration"
                id="duration_manual"
                placeholder="00:00"
                readOnly={false}
                maxLength={5}
                value={watch("duration")}
                onChange={(e) => {
                  setValue("duration", e.target.value);
                  trigger("duration");
                }}
                title="Enter duration manually (format: HH:MM)"
              />
            ) : (
              <Input
                label="Duration"
                id="duration"
                placeholder="Duration"
                readOnly={true}
                maxLength={50}
                value={calculateDuration()}
                className="text-[#6C6C6C] font-medium"
                title="Auto-calculated from time entries"
              />
            )}
          </div>

          <div className="mt-4 mb-4 flex items-center gap-2">
            <input
              id="billable"
              type="checkbox"
              className="h-4 w-4 text-[#73B5C5] border-gray-300 rounded cursor-pointer"
              checked={watch("billable")}
              onChange={(e) => {
                setValue("billable", e.target.checked);
              }}
            />
            <label
              htmlFor="billable"
              className="text-[14px] font-[500] text-[#1E1E1E]"
            >
              Billable to HLJ
            </label>
          </div>

          <div>
            <TextArea
              label="Notes"
              id="notes"
              placeholder="Notes"
              value={watch("notes")}
              onChange={(e) => {
                setValue("notes", e.target.value);
                trigger("notes");
              }}
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2 rounded-b-[10px]">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-[14px] w-[147px] text-gray-700 bg-[#F5F5F5] rounded-md hover:bg-gray-50 cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="w-[147px] h-[42px] bg-[#73b5c5] text-white text-[14px] font-[500] border-none rounded-[4px] cursor-pointer transition-colors hover:bg-[#5a9aaa] disabled:bg-[#9e9e9e] disabled:cursor-not-allowed flex items-center justify-center"
            disabled={
              !isValid ||
              isLoading ||
              !isFormModified ||
              // Disable if neither case is satisfied:
              // Case 1: All time fields are filled
              // Case 2: Clock in date and duration are filled
              !(
                (watch("clock_in_date") &&
                  watch("clock_in_time") &&
                  watch("clock_out_date") &&
                  watch("clock_out_time")) ||
                (watch("clock_in_date") &&
                  watch("duration") &&
                  watch("duration") !== "00:00")
              )
            }
          >
            {isLoading ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {"Updating..."}
              </>
            ) : (
              <>{"Update"}</>
            )}
          </button>
        </div>
      </form>
      {/* Modal for exceeding hours confirmation */}
      <Modal
        isOpen={isExceedingHoursModalOpen}
        onClose={() => {
          setIsExceedingHoursModalOpen(false);
          setPendingSubmitData(null);
        }}
        title="Exceeding Hours"
        width="min(95%, 550px)"
      >
        <div className="py-4">
          <p className="text-center text-[18px] font-[500] text-[#212121] mb-6">
            The clocked time exceeds the project's estimated hours. Do you still
            want to proceed?
          </p>
          <div className="flex justify-center gap-4">
            <button
              onClick={() => {
                if (pendingSubmitData) {
                  onSubmit(pendingSubmitData);
                  setPendingSubmitData(null);
                }
                setIsExceedingHoursModalOpen(false);
              }}
              className="px-3 py-2 w-[120px] text-[14px] font-medium h-[36px] bg-[#73B5C5] text-white rounded-md hover:bg-[#5da7b8] transition-colors cursor-pointer"
            >
              Yes, update
            </button>
            <button
              onClick={() => {
                setIsExceedingHoursModalOpen(false);
                setPendingSubmitData(null);
              }}
              className="px-3 py-2 w-[98px] h-[36px] text-[14px] font-medium bg-[#F5F5F5] text-gray-800 rounded-md hover:bg-gray-200 transition-colors cursor-pointer"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal for exceeding hours confirmation */}
      <Modal
        isOpen={showClearTimeModal}
        onClose={() => {
          setShowClearTimeModal(false);
        }}
        title="Confirm Edit Duration"
        width="min(95%, 550px)"
      >
        <div className="py-4">
          <p className="text-center text-[18px] font-[500] text-[#212121] mb-6">
            Editing the duration will clear the clock-in time, clock-out date,
            and clock-out time. Do you want to continue?
          </p>
          <div className="flex justify-center gap-4">
            <button
              onClick={handleClearTime}
              className="px-3 py-2 w-[120px] text-[14px] font-medium h-[36px] bg-[#73B5C5] text-white rounded-md hover:bg-[#5da7b8] transition-colors cursor-pointer"
            >
              Yes, Confirm
            </button>
            <button
              onClick={() => {
                setShowClearTimeModal(false);
              }}
              className="px-3 py-2 w-[98px] h-[36px] text-[14px] font-medium bg-[#F5F5F5] text-gray-800 rounded-md hover:bg-gray-200 transition-colors cursor-pointer"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default EditTimeEntryForm;
